import { CommonModule } from "@angular/common";
import { Component, computed, effect, signal } from '@angular/core';
import { FormsModule, Validators } from "@angular/forms";
import { MarkdownModule } from "ngx-markdown";
import { EditorConfig } from "ckeditor5";
import { toSignal } from "@angular/core/rxjs-interop";
import { AutoCompleteCompleteEvent } from "primeng/autocomplete";

// EZ
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { DocMeta } from "@template-ui/decorators";
import { FormValuesObject, EzFormInputType, InputConfig, InputWidthType, NamedEvent, PanelHeaderConfig, InputSelectOption, Primitive  } from "@template-ui/types";
import InsertTextButton from "@template-ui/ckEditor/plugins/InsertTextButton";
import ToolbarRightText from "@template-ui/ckEditor/plugins/ToolbarRightText";
import { EzBaseComponent, EzFormComponent } from "@template-ui/components";


@DocMeta({
  hide: true,
  showcaseTitle: "API Sample",
  showcaseDesc: `
  ## Ez Form API Sample
  The Ez form can be configured to make an API call to update data by setting the \`saveEndpoint\` input.
  The update can be triggered manually by calling the form's UpdateForm() method or by setting \`[useAutoSave]="true"\`.

  In this sample \`[useAutoSave]\` is true which means that an update call is made immediately upon any change of any
  input. The update can also be manually triggered with the Update button.

  For this sample, a mock data interceptor is used so that no actual API calls are made but you can see the
  updates in the console log.

  NOTE: Although this sample is configured to show green checkmarks upon a successful save, this functionality
  is still under development and not yet ready for prime time.

  `
})
@Component({
    selector: 'ui-form-api-sample, ez-form-api-sample',
    imports: [
        // Angular
        CommonModule, FormsModule,
        // Third party
        MarkdownModule,
        // EZ
        UiLibraryModule,
    ],
    templateUrl: './ez-form-api-sample.component.html',
    styleUrl: './ez-form-api-sample.component.scss'
})
export class EzFormApiSampleComponent extends EzBaseComponent {

    isDisabled: boolean = false;

    addActFormValues: {
      width: InputWidthType,
    } = {width: '4x'};

    // Example of getting data without any use of RxJs (except toSignal)
    recordsetSignal = toSignal(this.crudService.GetList("mock/StarWarsActors"));

    // Example of get/set to bind a local variable to a property of formValues
    get notableRole(): string {
      const v = this.formValues ? this.formValues['notableRole'] : undefined;
      return (v ?? '') as string;
    }
    set notableRole(value: string) {
      // Ensure formValues is initialized before setting the property to avoid "Object is possibly 'undefined'"
      this.formValues = { ...(this.formValues || {}), notableRole: value };
      this.addActFormValues = { ...(this.addActFormValues || {})};
    }

    // Holds the data
    recordset: Record<string, Primitive>[] = [];

    // Index for the array above
    recordIndex = 0;

    // A single record
    formValues?: FormValuesObject = {font: "Calibri"};

    // And object to display in the controls section
    delta: unknown;

    // Signal mapped to an HTML control to show the record num in the header
    recordNumSignal = signal<string>('');

    query = signal<string>('');

    // Get the otherChar suggestions as a signal based on another signal which is the query
    suggestions = computed(() => {

      const q = (this.query() ?? '').toLowerCase();
      const rs = this.recordsetSignal()?.data ?? [];
      return rs.filter(r => String(r['notableRole'] ?? '').toLowerCase().includes(q));
    });

    characterOptionsSig = computed<InputSelectOption[]>(() => {
      const data = this.recordsetSignal()?.data ?? [];
      return data.map(r =>
          ({
            value: r['notableRole'],
            viewValue: r['notableRole']
          }) as InputSelectOption
        );
    });

      additionalActions: InputConfig[] = [
      {
        type: EzFormInputType.HTML,
        label: 'Width:',
        name: 'WidthLabel'
      },
      {
        type: EzFormInputType.RADIO_GROUP_PLAIN,
        name: 'width',
        value: this.addActFormValues.width, // '4x' Shouldn't have to do this cuz it's bound to addActFormValues but that wasn't working on initial load
        options: [
          {value: '4x', viewValue: '4x'},
          {value: '5x', viewValue: '5x'},
          {value: 'fill', viewValue: 'fill'}
        ]
      },
      {
        type: EzFormInputType.BUTTON,
        name: 'prev',
        buttonText: '< Prev',
        disabled: () => this.recordIndex == 0
      },
      {
        type: EzFormInputType.HTML,
        name: 'recordMsg',
        width: "1x",
        label: this.recordNumSignal
      },
      {
        type: EzFormInputType.BUTTON,
        name: 'next',
        buttonText: 'Next >',
        disabled: () => this.recordIndex >= this.recordset.length - 1
      }
    ];

    panelHeaderConfig: PanelHeaderConfig = {
      additionalActions: this.additionalActions,
    };


    constructor()
    {
      super();
      this.thisClassName = 'EzFormApiSampleComponent';

      effect(() => {

        this.recordset = this.recordsetSignal()?.data || [];

        if(this.recordset?.length > this.recordIndex)
        {
          this.formValues = this.recordset[this.recordIndex];
          this.setRecord();
        }
      });

    }

  //actorInfoInputsConfig?: InputConfig[] ;

  lNameUpper_label = signal<string>('Hi');
  lNameTitle_label = signal<string>('There');

  lNameUpper_text = computed(() =>{return this.lNameUpper_label() + ' ' });
  lNameTitle_text = computed(() =>{return this.lNameTitle_label() + ' ' });

  editorConfig = {
    extraPlugins: [ InsertTextButton, ToolbarRightText ],
    toolbar: {
      items: [
        'insertLname_UPPER','insertLname_Title',
        'bold','italic','underline','bulletedList','numberedList','outdent','indent',
        'ezToolbarSpacer' ,'ezToolbarText'
      ],
      shouldNotGroupWhenFull: true
    },
    //insertTextButtonsInjector: this.injector,            // << REQUIRED FOR SIGNAL REACTIVITY Not anymore!
    toolbarRightText: "Applicant's Notes for Interview",
    placeholder: "Applicant's Notes for Interview",
    customInsertButtons: [
      { text: this.lNameUpper_text, label: this.lNameUpper_label, commandName: 'insertLname_UPPER' },
      { text: this.lNameTitle_text, label: this.lNameTitle_label, commandName: 'insertLname_Title' },
    ]
  } as EditorConfig;

  fontSignal = signal<string>("Calibri");

  richTextConfig = {
    type: EzFormInputType.RICH_TEXT, name: "biography", label: "Biography Rich Text", width: "3x"
    , editorConfig: this.editorConfig
    , editorFont: this.fontSignal
  } as InputConfig


    actorInfoInputsConfig: InputConfig[] = [

      { type: "text", name: "actorId", label: "Actor ID", width: "1x", validation: [Validators.required], readOnly: true },
      { type: EzFormInputType.PASSWORD, name: "firstName_pwd", field: "firstName", label: "Password (maps to first name)", width: "1x"},
      { type: EzFormInputType.TEXT,     name: "firstName_1", field:"firstName", label: "First Name", width: "1x", validation: [Validators.required], validationMessages: { required: 'First Name is required' } },

      { type: "text", name: "lastName", label: "Last Name", width: "1x", validation: [Validators.required], validationMessages: { required: 'Last Name is required' }  },
      //{ type: "text", name: "notableRole", label: "Notable Star Wars Role with a really really really long label", width: "1x", showLabelOnTooltip: true },
      { type: EzFormInputType.DATE, name: "dateOfBirth_1", field:"dateOfBirth", label: "Date of Birth", width: "1x" },
      { type: EzFormInputType.DATE_TIME, name: "dateAndTimeOfBirth", field:"dateOfBirth", label: "Date of Birth (with time)", width: "1x" },
      {
        type: EzFormInputType.CHECKBOX,
        name: "isLiving",
        label: "Is Living?",
        width: "1x"
      },
      { type: EzFormInputType.NUMBER, name: "yearsActive", label: "Years Active (since)", width: "1x"  },
      {
        type: EzFormInputType.SELECT,
        name: "trilogy",
        label: "Trilogy Debut (in drop-down)",
        customTooltip: "This is a custom tooltip that I just made up. It has nothing to do with the label.",
        width: "1x",
        options: [
          { value: "original", viewValue: "Original Trilogy" },
          { value: "prequel", viewValue: "Prequel Trilogy" },
          { value: "sequel", viewValue: "Sequel Trilogy" },
          { value: "spinoff", viewValue: "Spin-off" }
        ]
      },
      { name: "otherChar", type: EzFormInputType.AUTO_COMPLETE, label: "Another Character (with a long label)",
        autoCompleteConfig: {
          //endpoint: "mock/StarWarsActors",
          showDropdown: true,
          suggestions: this.suggestions,
          minQueryLength: 1,
          forceSelection: true,
          optionLabel: 'notableRole',
          optionValue: 'notableRole',
          itemHtmlTemplate:
          `
            <table style="width:100%; border-collapse:collapse; border:1px solid #d1d5db; font-size:0.875rem;" class="ac-item-inert">
              <tr>
                <td style="border:1px solid #374151; padding:4px 8px;">{{firstName}}</td>
                <td style="border:1px solid #374151; padding:4px 8px;">{{notableRole}}</td>
              </tr>
            </table>`
          // itemHtmlTemplate: `<span style="pointer-events:none">Some char: {{notableRole}}</span>`
          // itemHtmlTemplate: `{{notableRole}}`
        }
      },
      { name: "props", type: EzFormInputType.MULTI_SELECT, label: "Properties (multi-select)",
        options: [
            { value: 'hasLightsaber', viewValue: 'Has lightsaber' },
            { value: 'isPilot', viewValue: 'Is a pilot' },
            { value: 'isVillain', viewValue: 'Is a villan' },
            { value: 'isMainTrilogyCharacter', viewValue: 'Is a main trilogy character' }
        ]
      },
      {
        name: "characterAutocomplete", type: EzFormInputType.SELECT, label: "Character (auto-complete)",
        selectWithAutocomplete: true,
        autoCompleteConfig: {
          showAddNewInFooter: true,
          addNewButtonText: "Add Character",
        },
        options: this.characterOptionsSig
        // options: [
        //     { value: 'hasLightsaber', viewValue: 'Has lightsaber' },
        //     { value: 'isPilot', viewValue: 'Is a pilot' },
        //     { value: 'isVillain', viewValue: 'Is a villan' },
        //     { value: 'isMainTrilogyCharacter', viewValue: 'Is a main trilogy character' }
        // ]
      },

      { type: EzFormInputType.RADIO_GROUP, name: "trilogy", label: "Trilogy Debut (as vertical radio buttons 2x)", width: "2x",
        customCssClasses: 'vertical',
        options: [
          { value: "original", viewValue: "Original Trilogy" },
          { value: "prequel", viewValue: "Prequel Trilogy" },
          { value: "sequel", viewValue: "Sequel Trilogy" },
          { value: "spinoff", viewValue: "Spin-off" }
        ]
      },
      { type: EzFormInputType.TEXTAREA, name: "awards", label: "Awards", width: "2x" },
      { type: EzFormInputType.RADIO_GROUP, name: "trilogy", label: "Trilogy Debut (as radio buttons 4x)", width: "4x",
        options: [
          { value: "original", viewValue: "Original Trilogy" },
          { value: "prequel", viewValue: "Prequel Trilogy" },
          { value: "sequel", viewValue: "Sequel Trilogy" },
          { value: "spinoff", viewValue: "Spin-off" }
        ]
      },
      { type: EzFormInputType.HTML, label: "Trilogy (plain radio):", name: "trilogy_label"},
      { type: EzFormInputType.RADIO_GROUP_PLAIN, name: "trilogy", width: "3x",
        options: [
          { value: "original", viewValue: "Original Trilogy" },
          { value: "prequel", viewValue: "Prequel Trilogy" },
          { value: "sequel", viewValue: "Sequel Trilogy" },
          { value: "spinoff", viewValue: "Spin-off" }
        ]
      },
      {
        type: EzFormInputType.HTML,
        name: "imdbUrl",
        //label: "IMDB URL",
        width: "1x",
        label: `<span class="mt-3" style="top: 5px;">IMDB: <a class="auto-external-icon" href="{{imdbUrl}}" target="_blank">{{firstName}} {{lastName}}</a></span>` // An html control can use a template for the value
      },
      {
        type: "number",
        name: "academyAwardNominations",
        label: "Academy Award Nominations",
        width: "1x",
      },
      {
        name: 'flags',
        label: 'Flags',  // Doesn't use label?
        type: EzFormInputType.CHECKBOX_GROUP,
        options: [
          { value: "isForceUser", viewValue: 'Force User' },
          { value: false, viewValue: 'Jedi' },
          { value: false, viewValue: 'Sith' },
          { value: false, viewValue: 'Droid' }
        ],

        width: '2x',
      },
      { type: EzFormInputType.RADIO_GROUP, name: "trilogy", label: "Trilogy Debut (as radio buttons 3x)", width: "3x",
        options: [
          { value: "original", viewValue: "Original Trilogy" },
          { value: "prequel", viewValue: "Prequel Trilogy" },
          { value: "sequel", viewValue: "Sequel Trilogy" },
          { value: "spinoff", viewValue: "Spin-off" }
        ]
      },
      {
        type: EzFormInputType.SELECT, name: "font", label: "Font for Bibliography", width: "1x",
        value: "Calibri",
        options: [
          { value: "Inter",          viewValue: "Inter" },
          { value: "Roboto",         viewValue: "Roboto" },
          { value: "Open Sans",      viewValue: "Open Sans" },
          { value: "Consolas",       viewValue: "Consolas" },
          { value: "Century Gothic", viewValue: "Century Gothic" },
          { value: "Calibri",        viewValue: "Calibri" },
          { value: "Arial",          viewValue: "Arial" },
          { value: "Tahoma",         viewValue: "Tahoma" }
        ]
      },
      {
        name: 'flags',
        label: 'Flags',
        type: EzFormInputType.CHECKBOX_GROUP,
        customCssClasses: 'vertical',
        options: [
          { value: "isForceUser", viewValue: 'Force User' },
          { value: false, viewValue: 'Jedi' },
          { value: false, viewValue: 'Sith' },
          { value: false, viewValue: 'Droid' }
        ],

        width: '1x',
      },
      {
        type: EzFormInputType.RICH_TEXT, name: "biography", label: "Biography Rich Text", width: "3x"
        , editorConfig: this.editorConfig
        , editorFont: this.fontSignal
      },

      { type: EzFormInputType.FILE_CHOOSER, name: "file", label: "Choose a file?", width: "4x"},
      { type: "hidden", name: "id", readOnly: true },
      { type: "hidden", name: "isForceUser" }
    ];

    onAutoCompleteCompleted(e: NamedEvent<AutoCompleteCompleteEvent>)
    {
      // Grap the query
      const query = e.event.query;

      this.query.set(query);
    }

    onPanelHeaderButtonClick(e: NamedEvent)
    {
      this.dlog(`${e.name} button clicked`);

      switch(e.name)
      {
        case "next":
          if (this.recordIndex < this.recordset.length - 1)
          {
            this.recordIndex++;
          }
          break;

        case "prev":
          if (this.recordIndex > 0)
          {
            this.recordIndex--;
          }
          break;
      }
      this.formValues = this.recordset[this.recordIndex];

      this.delta = undefined;

      this.setRecord();
    }

    onFormUpdated(event: unknown)
    {
      //alert(`onFormUpdated: ${event}`);
    }

    formValuesChange(formValues: FormValuesObject)
    {
      this.fontSignal.set(formValues['font'] as string);
    }
    setRecord()
    {
      // Just in case setRecord is called in some context that doesn't force a redraw right away.
      requestAnimationFrame(() => {
        this.recordNumSignal.set(`Record ${this.recordIndex + 1} of ${this.recordset?.length || 0}`);

        this.setInsertButtons();
        //this.configureForms();
      });
    }

    setInsertButtons()
    {
      const lname = this.formValues!['lastNameUpper'] as string;

      if(lname)
      {
        this.lNameUpper_label.set(lname.toUpperCase());

        // Convert to upper first just to test smartTitleCase
        this.lNameTitle_label.set(this.ezText.resolveTemplate("{{name | smartTitleCase}}", {name: lname.toUpperCase()}));
      }
    }

    stripMetadata(obj: object)
    {
      return EzFormComponent.stripProxyMetadata(obj);
    }

    /**
     * This is just so we don't see unecessary stuff on the sample page.
     * When the form updates, it does this automaticlly so we don't send metadata (_isProxy) to the api.
     */
    get friendlyFormValues() {
      const bio = ((this.formValues?.['biography'] ?? '') as string).trimWithEllipsis(35);
      const rtn = this.stripMetadata({...this.formValues, ...{biography: bio}});
      return rtn;
    }

  }
